<script setup lang="ts">
import { useRouter } from 'vue-router'
import PaginateWithCount from "@/components/PaginateWithCount.vue"
import getUserRolesResponseType from "@/types/responses/getUserRoles"
import getPaginateUserRolesResponseType from "@/types/responses/getPaginateUserRoles"
import getUserRolesRequestType from "@/types/requests/getUserRoles"
import AuthorityCacheApi from "@/services/models/AuthorityCacheApi"
import UserRoleList from "@/components/UserRoles/UserRoleList.vue"
import SearchForm from "@/components/UserRoles/SearchForm.vue"
import { useAuth } from "@/services/user/Auth0UserProvider"
import UserRoleService from "@/services/models/UserRole";
import SelectRoleChecker from '@/router/selectRoleChecker'
import { MANAGEMENT_OFFICE, SUB_MANAGEMENT_OFFICE } from "@/services/consts/user_role";
import { Ref, ref, onMounted, watch } from 'vue'
import Api from "@/services/api/ApiServiceFabrick"
import i18n from '@/lang/i18n'
import { saveDataToSessionStorage, loadDataFromSessionStorage, clearSessionStorage } from "@/services/functions/sessionStorage";

document.title = i18n.global.t('pages.userRoles.title.listTitle')
const { loginWithRedirect } = useAuth()
const router = useRouter()

const userRoles: Ref<getUserRolesResponseType[]> = ref([])
const isLoading: Ref<boolean> = ref(false)
const isSearchCompleted: Ref<boolean> = ref(false)
const createAuthority: Ref<boolean> = ref(false)
const deleteAuthority: Ref<boolean> = ref(false)
const searchForm: Ref<getUserRolesRequestType> = ref({
  display_name: "",
  department: {},
  organization: {},
})
const totalPage: Ref<number> = ref(0)
const itemsPerPage: Ref<number> = ref(15)
const allElementsCount: Ref<number> = ref(0)
const currentPage: Ref<number> = ref(1)
const errors = ref({
  organization: [],
  department: [],
  display_name: []
} as {organization: string[], department: string[], display_name: string[]})

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => {value: any}
}>()

onMounted(() => {
  getAuthorities()
  firstSearchList()
})

watch(currentPage, () => {
  const param = makeParam(searchForm.value)
  getUserRoles(param)
})

async function getAuthorities() {
  const authorityList = await AuthorityCacheApi.getWithParse()
  createAuthority.value = !!authorityList["octlink.user_role.create"]
  deleteAuthority.value = !!authorityList["octlink.user_role.delete"]
}

function moveToUserRoleForm() {
  router.push('/user-roles/form/')
}

function firstSearchList(){
  const current_role = SelectRoleChecker.getCurrentRole()
  let organization_type = ""
  if (current_role?.organization_type) {
    organization_type = current_role.organization_type
  }
  const savedSearchParam = loadDataFromSessionStorage('userRoleSearchParam');
  if (savedSearchParam) {
    getUserRoles(savedSearchParam as getUserRolesRequestType)
  } else if (organization_type == SUB_MANAGEMENT_OFFICE.type) {
    const param = makeParam(searchForm.value)
    getUserRoles(param)
  }
}

function searchList (form: getUserRolesRequestType) {
  searchForm.value = form
  currentPage.value = 1
  const param = makeParam(form)
  getUserRoles(param)
  saveDataToSessionStorage('userRoleSearchParam', param)
}

function makeParam(form: getUserRolesRequestType){
  let param = {} as getUserRolesRequestType
  param.display_name = form.display_name
  if (form.department && form.department.id) {
    param.department_id = form.department.id
    param.department_name = form.department.name
  }
  if (form.organization) {
    param.office_id = form.organization.id
    param.office_type = UserRoleService.converetOfficeType(form.organization.type)
  }
  param.page=currentPage.value
  return param
}

function getUserRoles(searchParams: getUserRolesRequestType) {
  isLoading.value = true
  isSearchCompleted.value = true
  Api().getUserRoles(searchParams).then((data: getPaginateUserRolesResponseType) => {
    currentPage.value = data.current_page
    totalPage.value = data.last_page
    allElementsCount.value = data.total
    itemsPerPage.value = data.per_page
    userRoles.value = data.data
    isLoading.value = false
  }).catch((error: any) => {
    isLoading.value = false
    isSearchCompleted.value = false
    return errorHandle(error.response)
  })
}

function errorHandle (res: any) {
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      break
    case 403:
      props.showFlash(i18n.global.t('pages.common.messages.forbidden'), "error", true)
      break
    case 422:
      errors.value = formatErrorMessages(res.data.errors)
  }
}

function formatErrorMessages(errors){
  let errorMessages = {
    organization: [] as string[],
    department: [] as string[],
    display_name: [] as string[]
  }
  if (errors.office_id) {
    errorMessages.organization = errors.office_id
  }
  if (errors.department_id) {
    errorMessages.department = errors.department_id
  }
  if (errors.display_name) {
    errorMessages.display_name = errors.display_name
  }
  return errorMessages
}

function setSearchErrors(errorMessages: {organization: string[], department: string[], display_name: string[]}){
  errors.value = errorMessages;
}

function deleteUserRole(id: number){
  let newUserRoles = [] as getUserRolesResponseType[]
  userRoles.value.forEach( (role) => {
    if (role.id != id) {
      newUserRoles.push(role)
    }
  })
  userRoles.value = newUserRoles
}

function clearUserRoleSearchParam() {
  clearSessionStorage('userRoleSearchParam')
}

</script>
<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex justify-content-between mb-1">
        <h4 class="card-title mt-0">
          {{ i18n.global.t('pages.userRoles.title.listTitle') }}
        </h4>
      </div>
      <div class="mb-5 d-flex justify-end">
        <div class="btn-area">
          <v-btn
            v-if="createAuthority"
            class="exec-btn mt-0"
            @click="moveToUserRoleForm"
          >
            {{ i18n.global.t('pages.common.button.newCreate') }}
          </v-btn>
        </div>
      </div>
      <div>
        <UserRoleList
          class="mb-2"
          :userRoles="userRoles"
          :isLoading="isLoading"
          :isSearchCompleted="isSearchCompleted"
          :deleteAuthority="deleteAuthority"
          :showFlash="showFlash"
          @deleteUserRole="deleteUserRole"
        />
        <PaginateWithCount
          class="justify-end"
          :totalPage="totalPage"
          v-model="currentPage"
          :itemsPerPage="itemsPerPage"
          :allElementsCount="allElementsCount"
          :showItemCount="true"
        />
      </div>
    </div>
    <SearchForm
      :isLoading="isLoading"
      :errors="errors"
      @search="searchList"
      @setErrors="setSearchErrors"
      @clear="clearUserRoleSearchParam"
    />
  </div>

</template>
<style scoped>
.content {
  min-width: 58vw;
}
</style>
